import { api } from "@/redux/api";

import { RequestPaginationParams } from "@/types/request-params";
import {
	AssignStatus,
	InclusionFilterStatus,
	PriorityFilterStatus,
	SelectType,
	SignalFilterStatus,
} from "@/enum/list.enum";

import {
	CreateDeepResearchPrompt,
	DeepResearchPrompt,
	DeepResearchPromptResponse,
	DeepResearchPromptResponseSchema,
	DeepResearchPromptSchema,
	UpdateDeepResearchPrompt,
} from "../../pages/Templates/pages/DeepResearchPrompts/schema/deep-research-prompt";

import {
	CreatePinnedFactRequest,
	DeepResearchResponseInfo,
	DeepResearchPending,
	DeepResearchPendingSchema,
	DeepResearchResponsesListSchema,
	GeneratePromptSuggestionsRequest,
	ListDeepResearchResponsesParams,
	PinnedFact,
	PinnedFactSchema,
	PinnedFactsListSchema,
	PromptSuggestionsResponse,
	PromptSuggestionsResponseSchema,
} from "./schema";

import type { IFilterWithValues } from "../../pages/List/components/FilterModal";

export type ExecuteBulkDeepResearchListRequest = {
	listId?: number;
	listPersonIds?: number[];
	selectType: SelectType;
	deepResearchPromptId?: number;
	inclusionFilterStatus?: InclusionFilterStatus[];
	priorityFilterStatus?: PriorityFilterStatus[];
	assignStatus?: AssignStatus[];
	filter?: IFilterWithValues;
	/** The table's search: a select-all made while searching covers the matches only (PREC-4443). */
	searchValue?: string;
	/** Every status of the tab the table showed; the Assigned tab stands for several (PREC-4450). */
	assignStatuses?: AssignStatus[];
	/** The reason sub-filters the table showed, "STATUS:filterParam" (PREC-4450). */
	exclusionReasons?: string[];
	signalFilterStatus?: SignalFilterStatus[];
};

export const deepResearchApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getDeepResearchPrompts: builder.query<DeepResearchPromptResponse, RequestPaginationParams>({
			query: ({ sorting, ...params }) => ({
				url: "deep-research/prompts",
				params: {
					...(params && { ...params }),
					...(sorting && { ...sorting }),
				},
				responseSchema: DeepResearchPromptResponseSchema,
			}),
			providesTags: (result) =>
				result?.data.map((item) => ({ type: "DeepResearchPrompt", id: item.id })) || [],
		}),
		getDeepResearchPrompt: builder.query<DeepResearchPrompt, number>({
			query: (id) => ({
				url: `deep-research/prompts/${id}`,
				responseSchema: DeepResearchPromptSchema,
			}),
			providesTags: (result) => [{ type: "DeepResearchPrompt", id: result?.id }],
		}),
		createDeepResearchPrompt: builder.mutation<DeepResearchPrompt, CreateDeepResearchPrompt>({
			query: (body) => ({
				url: "deep-research/prompts",
				method: "POST",
				body,
				responseSchema: DeepResearchPromptSchema,
			}),
			invalidatesTags: ["DeepResearchPrompt"],
		}),
		updateDeepResearchPrompt: builder.mutation<DeepResearchPrompt, UpdateDeepResearchPrompt>({
			query: ({ id, ...body }) => ({
				url: `deep-research/prompts/${id}`,
				method: "PATCH",
				body,
				responseSchema: DeepResearchPromptSchema,
			}),
			invalidatesTags: (result) => [{ type: "DeepResearchPrompt", id: result?.id }],
		}),
		deleteDeepResearchPrompt: builder.mutation<{ id: number }, number>({
			query: (id) => ({
				url: `deep-research/prompts/${id}`,
				method: "DELETE",
			}),
			invalidatesTags: (result) => [{ type: "DeepResearchPrompt", id: result?.id }],
		}),
		getDeepResearchResponses: builder.query<
			DeepResearchResponseInfo[],
			ListDeepResearchResponsesParams
		>({
			query: (params) => ({
				url: "deep-research/responses",
				params: {
					...(params.prospectId && { prospectId: params.prospectId }),
					...(params.limit && { limit: params.limit }),
				},
				responseSchema: DeepResearchResponsesListSchema,
			}),
		}),
		// Server truth for where a research stands — queued, running or failed (PREC-4218, PREC-4052).
		// The page keeps no stamp of its own: one did not survive a reload in the extension.
		getDeepResearchPending: builder.query<DeepResearchPending, number[]>({
			query: (prospectIds) => ({
				url: "deep-research/pending",
				params: { prospectIds: prospectIds.join(",") },
				responseSchema: DeepResearchPendingSchema,
			}),
			keepUnusedDataFor: 0,
		}),
		executeDeepResearch: builder.mutation<
			void,
			{ prospectId: number; deepResearchPromptId?: number }
		>({
			query: (body) => ({
				url: "deep-research/execute",
				method: "POST",
				body,
			}),
		}),
		executeBulkDeepResearch: builder.mutation<
			{ queued: number; skipped: number; total: number },
			{
				listId: number;
				selectType: SelectType;
				trackerOrganizationIds: number[];
				deepResearchPromptId: number;
				filter?: IFilterWithValues;
			}
		>({
			query: (body) => ({
				url: "deep-research/execute-bulk",
				method: "POST",
				body,
			}),
		}),
		executeBulkDeepResearchList: builder.mutation<
			{ queued: number; skipped: number; total: number },
			ExecuteBulkDeepResearchListRequest
		>({
			query: (body) => ({
				url: "deep-research/execute-bulk-list",
				method: "POST",
				body,
			}),
		}),
		generatePromptSuggestions: builder.mutation<
			PromptSuggestionsResponse,
			GeneratePromptSuggestionsRequest
		>({
			query: (body) => ({
				url: "deep-research/prompt-suggestions",
				method: "POST",
				body,
				responseSchema: PromptSuggestionsResponseSchema,
			}),
		}),
		getPinnedFacts: builder.query<PinnedFact[], number>({
			query: (prospectId) => ({
				url: "deep-research/pinned-facts",
				params: { prospectId },
				responseSchema: PinnedFactsListSchema,
			}),
			providesTags: (_result, _error, prospectId) => [
				{ type: "DeepResearchPinnedFact", id: prospectId },
			],
		}),
		pinResearchFact: builder.mutation<PinnedFact, CreatePinnedFactRequest>({
			query: (body) => ({
				url: "deep-research/pinned-facts",
				method: "POST",
				body,
				responseSchema: PinnedFactSchema,
			}),
			invalidatesTags: (_result, _error, { prospectId }) => [
				{ type: "DeepResearchPinnedFact", id: prospectId },
			],
		}),
		unpinResearchFact: builder.mutation<{ id: number }, { id: number; prospectId: number }>({
			query: ({ id }) => ({
				url: `deep-research/pinned-facts/${id}`,
				method: "DELETE",
			}),
			invalidatesTags: (_result, _error, { prospectId }) => [
				{ type: "DeepResearchPinnedFact", id: prospectId },
			],
		}),
	}),
});

export const {
	useGetDeepResearchPromptsQuery,
	useGetDeepResearchPromptQuery,
	useCreateDeepResearchPromptMutation,
	useUpdateDeepResearchPromptMutation,
	useDeleteDeepResearchPromptMutation,
	useGetDeepResearchResponsesQuery,
	useGetDeepResearchPendingQuery,
	useExecuteDeepResearchMutation,
	useExecuteBulkDeepResearchMutation,
	useExecuteBulkDeepResearchListMutation,
	useGeneratePromptSuggestionsMutation,
	useGetPinnedFactsQuery,
	usePinResearchFactMutation,
	useUnpinResearchFactMutation,
} = deepResearchApi;
